import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Select,
  MenuItem,
  TextField,
  InputLabel,
} from '@mui/material';
import FaceIcon from '@mui/icons-material/Face';
import DoneIcon from '@mui/icons-material/Done';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type ChipSize = 'small' | 'medium';
type ChipVariant = 'filled' | 'outlined';

const ChipShowcase = () => {
  const [color, setColor] = useState<ChipColor>('default');
  const [size, setSize] = useState<ChipSize>('medium');
  const [variant, setVariant] = useState<ChipVariant>('filled');
  const [label, setLabel] = useState('Chip');
  const [disabled, setDisabled] = useState(false);
  const [clickable, setClickable] = useState(false);
  const [deletable, setDeletable] = useState(false);
  const [icon, setIcon] = useState(false);
  const [avatar, setAvatar] = useState(false);

  const handleClick = () => {};

  const handleDelete = () => {};

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Chip Component
      </Typography>
      <Typography variant="body1" paragraph>
        The Chip component is used to represent small blocks of information. They are commonly used for tags, contacts,
        or filter tokens.
      </Typography>

      {/* Preview */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
          <Chip
            label={label}
            color={color}
            size={size}
            variant={variant}
            disabled={disabled}
            clickable={clickable}
            onDelete={deletable ? handleDelete : undefined}
            onClick={clickable ? handleClick : undefined}
            icon={icon ? <DoneIcon /> : undefined}
            avatar={avatar ? <FaceIcon /> : undefined}
          />
        </Box>
      </Paper>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <FormControl fullWidth>
            <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} size="small" />
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Color</InputLabel>
            <Select value={color} label="Color" onChange={(e) => setColor(e.target.value as ChipColor)}>
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Size</InputLabel>
            <Select value={size} label="Size" onChange={(e) => setSize(e.target.value as ChipSize)}>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Variant</InputLabel>
            <Select value={variant} label="Variant" onChange={(e) => setVariant(e.target.value as ChipVariant)}>
              <MenuItem value="filled">Filled</MenuItem>
              <MenuItem value="outlined">Outlined</MenuItem>
            </Select>
          </FormControl>

          <FormGroup>
            <FormControlLabel
              control={<Switch checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />}
              label="Disabled"
            />
            <FormControlLabel
              control={<Switch checked={clickable} onChange={(e) => setClickable(e.target.checked)} />}
              label="Clickable"
            />
            <FormControlLabel
              control={<Switch checked={deletable} onChange={(e) => setDeletable(e.target.checked)} />}
              label="Deletable"
            />
            <FormControlLabel
              control={<Switch checked={icon} onChange={(e) => setIcon(e.target.checked)} />}
              label="Show Icon"
            />
            <FormControlLabel
              control={<Switch checked={avatar} onChange={(e) => setAvatar(e.target.checked)} />}
              label="Show Avatar"
            />
          </FormGroup>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChipShowcase;
